import { inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { MBPError } from '@errors/mbp-error';

/**
 * Shared HTTP request logic. Subclasses supply only the API base URL, so admin
 * and client APIs reuse the same request/error handling without duplication.
 */
export abstract class HttpServiceBase {
  private readonly http = inject(HttpClient);

  protected abstract readonly baseUrl: string;

  public getObjectAsync<T>(path: string): Promise<T> {
    return firstValueFrom(
      this.http.get<T>(`${this.baseUrl}${path}`).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  public postObjectAsync<T, E>(path: string, body: T): Promise<E> {
    return firstValueFrom(
      this.http.post<E>(`${this.baseUrl}${path}`, body).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  public patchObjectAsync<T, E>(path: string, object: T): Promise<E> {
    return firstValueFrom(
      this.http.patch<E>(`${this.baseUrl}${path}`, object).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  public putObjectAsync<T, E>(path: string, body: T): Promise<E> {
    return firstValueFrom(
      this.http.put<E>(`${this.baseUrl}${path}`, body).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  public deleteAsync<E>(path: string): Promise<E> {
    return firstValueFrom(
      this.http.delete<E>(`${this.baseUrl}${path}`).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  public postBlobAsync<T>(path: string, body: T): Promise<HttpResponse<Blob>> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}${path}`, body, { observe: 'response', responseType: 'blob' }).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  public getBlobAsync(path: string): Promise<HttpResponse<Blob>> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}${path}`, { observe: 'response', responseType: 'blob' }).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => this.createMbpError(error)))));
  }

  private createMbpError(error: HttpErrorResponse): MBPError {
    const mbpError = new MBPError();
    mbpError.statusCode = error.status;
    mbpError.validationErrorCode = error.error?.validationErrorCode ?? error.error?.error?.validationErrorCode;
    mbpError.validationMessage = error.error?.validationMessage ?? error.error?.error?.validationMessage;
    mbpError.originalError = error;
    return mbpError;
  }
}
